import { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { GradeBand } from '@peekapak/types/EmotionsClassification';
import { values as checkInValues } from './CheckIn/checkInValues';
import {
  getElementaryMoodColorFill,
  getElementaryMoodTextColor,
  getSecondaryMoodTextColor,
} from './AdministratorDashboard/MoodboardAdmin/moodboardUtilities';

export interface EmotionColor {
  backgroundColor: string;
  textColor: string;
}

// DateRangePicker returns the selected end date at local midnight - without this,
// check-ins later that same day would be excluded from the range.
export function getEndOfDay(date: Date): number {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.getTime();
}

// checkInValues.ts is the actual 6-12 check-in UI - source of truth for these
// fill colors per PRD (tags must match what students see at check-in).
const SIX_TO_TWELVE_FILL: Record<string, string> = Object.fromEntries(
  checkInValues.map((category) => [category.title, category.segmentColor]),
);

// A 6-12 check-in's feeling is always a SecondaryMood sub-feeling - the check-in UI has no way to select a bare
// top-level category - so there's no need to check against Mood at all here (and doing so is wrong: a sub-feeling
// can share its string with an unrelated top-level Mood, e.g. SecondaryMood.SCARED.NERVOUS === Mood.NERVOUS).
function resolveSixToTwelveCategory(feeling: Mood | SecondaryMood): string {
  const parentEntry = Object.entries(SecondaryMood).find(([, subFeelings]) =>
    (Object.values(subFeelings) as string[]).includes(feeling),
  );

  if (!parentEntry) {
    throw new Error(`Invalid feeling: ${feeling}`);
  }

  const [parentKey] = parentEntry;
  return Mood[parentKey as keyof typeof Mood];
}

// Overrides the shared Moodboard fill for Sad/Angry to match this report's own mockup;
// left unset (falls through to the shared colors) for anything else.
const PK5_FILL_OVERRIDE: Partial<Record<string, string>> = {
  [Mood.SAD]: '#76BEE0',
  [Mood.ANGRY]: '#619ECC',
};

export function getEmotionColor(feeling: Mood | SecondaryMood, gradeBand: GradeBand): EmotionColor {
  if (gradeBand === 'PK-5') {
    return {
      backgroundColor: PK5_FILL_OVERRIDE[feeling] ?? getElementaryMoodColorFill(feeling),
      textColor: getElementaryMoodTextColor(feeling),
    };
  }

  const category = resolveSixToTwelveCategory(feeling);
  return {
    backgroundColor: SIX_TO_TWELVE_FILL[category],
    textColor: getSecondaryMoodTextColor(category),
  };
}
